import {
  ReadingStatus,
  ReadingType,
  ReadingTemplateRoles,
} from 'infrastructure/enums';
import { getReadingTypeLabel } from 'infrastructure/functions';

import { templateRoleBlurbs } from './blurbs';

import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type {
  IRpmPatientFormActivity,
  TFormItem,
  TFormReadingStatus,
  TFormsReadingType,
  TTemplateRoleData,
} from './types';

export const getUnique = (arr: IRpmPatientFormActivity[]) => {
  return arr.reduce<IRpmPatientFormActivity[]>((acc, obj) => {
    if (
      !acc.some(
        (o) =>
          o.readingType === obj.readingType &&
          o.globalStatus === obj.globalStatus,
      )
    ) {
      acc.push(obj);
    }
    return acc;
  }, []);
};

export const aggregateData = (initData: IActivityData[]) => {
  const dataWithPulse = initData.map((i) => {
    if (
      i.readingType === ReadingType.SleepingMat ||
      i.readingType === ReadingType.SleepingMatDuration ||
      i.readingType === ReadingType.SleepingMatActivity
    ) {
      return { ...i, readingType: ReadingType.SleepingMat };
    }
    return i;
  });

  const dataWithCurrentTypes = dataWithPulse.filter(
    (i): i is IRpmPatientFormActivity =>
      i.globalStatus !== ReadingStatus.Critical,
  );

  const uniqueData = getUnique(dataWithCurrentTypes);
  const groupedDataByReadingType = Object.groupBy(
    dataWithCurrentTypes,
    (v) => v.readingType,
  );
  const groupedDataByStatus = Object.groupBy(uniqueData, (v) => v.globalStatus);
  const riskReadings =
    groupedDataByStatus.risk?.map((v) => v.readingType) ?? [];

  const filteredDataByRiskPriority = Object.entries(groupedDataByStatus).reduce<
    Partial<Record<ReadingStatus, IRpmPatientFormActivity[]>>
  >((acc, [status, data]) => {
    if (status === ReadingStatus.Normal) {
      acc[status] = data.filter((v) => !riskReadings.includes(v.readingType));
    }
    if (status === ReadingStatus.Risk) {
      acc[status] = data;
    }

    return acc;
  }, {});

  const statusSortPriority: Record<ReadingStatus, number> = {
    risk: 0,
    normal: 1,
    critical: 2,
  };

  const flatData = Object.values(filteredDataByRiskPriority).flat();
  const sortedByStatusData = flatData.toSorted(
    (a, b) =>
      statusSortPriority[a.globalStatus] - statusSortPriority[b.globalStatus],
  );
  return { sortedByStatusData, flatData, groupedDataByReadingType };
};

export const scrollToErrorElement = (wrapper: HTMLDivElement | null) => {
  const elementWithError = wrapper?.querySelector('[class*="error"]');
  if (!elementWithError) return;

  elementWithError.scrollIntoView({
    block: 'start',
    behavior: 'smooth',
  });
};

export const checkIsErrorExist = <
  T extends Record<string, string | string[]> | undefined,
>(
  formItem: TFormItem,
  errorsByType: T,
) => {
  let isError: boolean = false;
  if (formItem.type === 'info') {
    isError = false;
  }

  const recursiveCheckItemsError = (items?: TFormItem[]): boolean => {
    return (
      items?.some((i) => {
        const isFormItemItemsArrayAndHasError =
          Array.isArray(errorsByType?.[i.name]) &&
          errorsByType?.[i.name]?.length;
        const isFormItemArrayAndHasError =
          Array.isArray(errorsByType?.[formItem.name]) &&
          errorsByType?.[formItem.name]?.length;

        const internalItemsError =
          i.type !== 'info' && recursiveCheckItemsError(i?.items);

        return (
          isFormItemItemsArrayAndHasError ||
          errorsByType?.[i.name] ||
          isFormItemArrayAndHasError ||
          errorsByType?.[formItem.name] ||
          internalItemsError
        );
      }) || false
    );
  };

  const formItemItemsError =
    (formItem.type !== 'info' && recursiveCheckItemsError(formItem?.items)) ||
    false;

  const formItemOptionsError =
    (formItem.type !== 'info' &&
      formItem?.options?.some((i) =>
        i?.subItems?.some(
          (j) =>
            (Array.isArray(errorsByType?.[j.name]) &&
              errorsByType?.[j.name]?.length) ||
            errorsByType?.[j.name],
        ),
      )) ||
    false;

  const formItemError = !!errorsByType?.[formItem.name];
  isError = formItemError || formItemItemsError || formItemOptionsError;

  return isError;
};

export function appendBlurbsForStatus<
  R extends ReadingTemplateRoles,
  S extends TFormReadingStatus,
  T extends TFormsReadingType,
>(
  templateRole: R,
  status: S,
  readingType: T,
  statusData: TTemplateRoleData[R][S][T],
): string {
  let blurb = '';
  const blurbsForStatus = templateRoleBlurbs[templateRole][status];
  const generateBlurb = blurbsForStatus?.[readingType] as (
    data: typeof statusData,
  ) => string;

  if (generateBlurb) {
    const generatedBlurb = generateBlurb(statusData);
    blurb += `${getReadingTypeLabel(readingType)}:\n`;
    blurb += generatedBlurb.startsWith('\n')
      ? generatedBlurb.slice(1)
      : generatedBlurb;
    blurb += '\n\n';
  }

  return blurb;
}

export const checkNurseContactWithPatientValue = (
  templateRole: ReadingTemplateRoles,
  data: TTemplateRoleData[ReadingTemplateRoles],
) => {
  const isNurse = templateRole === ReadingTemplateRoles.RegisterNurse;

  if (!isNurse) {
    return false;
  }

  const riskReadings = [
    data[ReadingStatus.Risk]?.BloodGlucose,
    data[ReadingStatus.Risk]?.BloodPressure,
    data[ReadingStatus.Risk]?.SpO2,
    data[ReadingStatus.Risk]?.Weight,
  ];

  return riskReadings.some(
    (reading) =>
      reading && 'contactWithPatient' in reading && reading.contactWithPatient,
  );
};
